import React, { useState } from "react";
import api from "../../api";
import logo from "../../img/social-white.png";
import "./PartnerFeedback.css";

const PartnerFeedback = ({ showToast }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    organizationLegalName: "",
    doingBusinessAs: "",
    website: "",
    mainLocationAddress: "",
    primaryContactName: "",
    primaryContactTitle: "",
    primaryContactEmail: "",
    primaryContactPhone: "",
    organizationType: "",
    usState: "",
    countiesServed: "",
    fundingSupport: [],
    needStatement: "",
    fundingSources: [],
    specificOpportunity: "",
    opportunityLink: "",
    submissionDeadline: "",
    applicationAuthor: "",
    grantExperience: "",
    largestGrant: "",
    budgetPreparation: "",
    weeklyCheckIns: "",
    documentsAvailable: [],
    fiscalSponsor: "",
    preferredSupportLane: "",
    additionalInfo: "",
    shareWithPartner: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked
        ? [...prev[fieldName], value]
        : prev[fieldName].filter(item => item !== value)
    }));
  };

  const validatePage = (page) => {
    switch(page) {
      case 1:
        if (!formData.organizationLegalName || !formData.primaryContactName || !formData.primaryContactEmail) {
          showToast?.("Please fill in all required fields", "error");
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.primaryContactEmail)) {
          showToast?.("Please enter a valid email address", "error");
          return false;
        }
        break;
      case 2:
        if (formData.fundingSupport.length === 0) {
          showToast?.("Please select at least one funding support option", "error");
          return false;
        }
        break;
      case 4:
        if (!formData.shareWithPartner) {
          showToast?.("Please indicate whether we may share with INCITE", "error");
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validatePage(currentPage)) {
      setCurrentPage(prev => Math.min(prev + 1, 4));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = (e) => {
    e.preventDefault();
    setCurrentPage(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePage(4)) return;

    setSubmitting(true);
    try {
      await api.post("/partner-feedback", formData);
      setSubmitted(true);
    } catch (error) {
      showToast?.(error.response?.data?.error || "Failed to submit application", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="partner-feedback-container">
        <div className="partner-feedback-success">
          <div className="success-icon">✓</div>
          <h2>Thank You!</h2>
          <p>Your grant application intake has been submitted successfully.</p>
          <p className="success-subtext">We will review your information and contact you within 2-3 business days.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="partner-feedback-container">
      <div className="partner-feedback-form">
        <div className="form-header">
          <img src={logo} alt="R3 Logo" className="form-logo" />
          <h1>Partner Feedback Form</h1>
        </div>

        <form onSubmit={handleSubmit}>
          {/* PAGE 1: Organization & Contact Information */}
          {currentPage === 1 && (
            <div className="form-page">
              <div className="form-group">
                <label htmlFor="organizationLegalName">
                  Organization legal name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="organizationLegalName"
                  name="organizationLegalName"
                  value={formData.organizationLegalName}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="doingBusinessAs">
                  Doing Business As / Program name (if different)
                </label>
                <input
                  type="text"
                  id="doingBusinessAs"
                  name="doingBusinessAs"
                  value={formData.doingBusinessAs}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="mainLocationAddress">Main Location Address</label>
                <textarea
                  id="mainLocationAddress"
                  name="mainLocationAddress"
                  value={formData.mainLocationAddress}
                  onChange={handleChange}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="primaryContactName">
                    Primary contact name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="primaryContactName"
                    name="primaryContactName"
                    value={formData.primaryContactName}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="primaryContactTitle">Primary contact title/role</label>
                  <input
                    type="text"
                    id="primaryContactTitle"
                    name="primaryContactTitle"
                    value={formData.primaryContactTitle}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="primaryContactEmail">
                    Primary contact email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="primaryContactEmail"
                    name="primaryContactEmail"
                    value={formData.primaryContactEmail}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="primaryContactPhone">Primary contact phone</label>
                  <input
                    type="tel"
                    id="primaryContactPhone"
                    name="primaryContactPhone"
                    value={formData.primaryContactPhone}
                    onChange={handleChange}
                    maxLength="10"
                    placeholder="1234567890"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="organizationType">Organization type</label>
                  <select
                    id="organizationType"
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleChange}
                  >
                    <option value="">Select type</option>
                    <option value="Nonprofit 501(c)(3)">Nonprofit 501(c)(3)</option>
                    <option value="Public agency (city/county/state)">Public agency (city/county/state)</option>
                    <option value="K‑12 school / District / College">K‑12 school / District / College</option>
                    <option value="Healthcare provider (e.g., FQHC, hospital)">Healthcare provider (e.g., FQHC, hospital)</option>
                    <option value="Tribal government or organization">Tribal government or organization</option>
                    <option value="Faith‑based organization">Faith‑based organization</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="usState">U.S. state/territory where services occur</label>
                  <input
                    type="text"
                    id="usState"
                    name="usState"
                    value={formData.usState}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="countiesServed">County(ies)/cities served</label>
                <textarea
                  id="countiesServed"
                  name="countiesServed"
                  value={formData.countiesServed}
                  onChange={handleChange}
                  rows="3"
                />
              </div>
            </div>
          )}

          {/* PAGE 2: Funding Details */}
          {currentPage === 2 && (
            <div className="form-page">
              <div className="form-group">
                <label>
                  What do you want this funding to support? (select all that apply) <span className="required">*</span>
                </label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Rapid Risk Reduction / ONEbox® deployment & placement"
                      checked={formData.fundingSupport.includes("Rapid Risk Reduction / ONEbox® deployment & placement")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSupport')}
                    />
                    <span>Rapid Risk Reduction / ONEbox® deployment & placement</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Overdose prevention education / micro‑courses"
                      checked={formData.fundingSupport.includes("Overdose prevention education / micro‑courses")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSupport')}
                    />
                    <span>Overdose prevention education / micro‑courses</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Behavioral health capacity/services"
                      checked={formData.fundingSupport.includes("Behavioral health capacity/services")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSupport')}
                    />
                    <span>Behavioral health capacity/services</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Data & evaluation (measuring outcomes)"
                      checked={formData.fundingSupport.includes("Data & evaluation (measuring outcomes)")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSupport')}
                    />
                    <span>Data & evaluation (measuring outcomes)</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Community outreach / communications"
                      checked={formData.fundingSupport.includes("Community outreach / communications")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSupport')}
                    />
                    <span>Community outreach / communications</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Other"
                      checked={formData.fundingSupport.includes("Other")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSupport')}
                    />
                    <span>Other</span>
                  </label>
                </div>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label htmlFor="needStatement">
                  Brief need statement (1–3 sentences): What problem are you solving, for whom, and why now?
                </label>
                <textarea
                  id="needStatement"
                  name="needStatement"
                  value={formData.needStatement}
                  onChange={handleChange}
                  rows="4"
                />
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label>Which funding source(s) are you pursuing?</label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Federal"
                      checked={formData.fundingSources.includes("Federal")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSources')}
                    />
                    <span>Federal</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="State"
                      checked={formData.fundingSources.includes("State")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSources')}
                    />
                    <span>State</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="County/City"
                      checked={formData.fundingSources.includes("County/City")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSources')}
                    />
                    <span>County/City</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Foundation/Corporate"
                      checked={formData.fundingSources.includes("Foundation/Corporate")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSources')}
                    />
                    <span>Foundation/Corporate</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Opioid Settlement dollars"
                      checked={formData.fundingSources.includes("Opioid Settlement dollars")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSources')}
                    />
                    <span>Opioid Settlement dollars</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Not sure / exploring"
                      checked={formData.fundingSources.includes("Not sure / exploring")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSources')}
                    />
                    <span>Not sure / exploring</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Other"
                      checked={formData.fundingSources.includes("Other")}
                      onChange={(e) => handleCheckboxChange(e, 'fundingSources')}
                    />
                    <span>Other</span>
                  </label>
                </div>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label>Do you have a specific opportunity (RFP/NOFO/FOA) in mind right now?</label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="specificOpportunity"
                      value="Yes"
                      checked={formData.specificOpportunity === "Yes"}
                      onChange={handleChange}
                    />
                    <span>Yes</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="specificOpportunity"
                      value="Not yet / exploring"
                      checked={formData.specificOpportunity === "Not yet / exploring"}
                      onChange={handleChange}
                    />
                    <span>Not yet / exploring</span>
                  </label>
                </div>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label htmlFor="opportunityLink">Opportunity link (URL) (if applicable)</label>
                <input
                  type="url"
                  id="opportunityLink"
                  name="opportunityLink"
                  value={formData.opportunityLink}
                  onChange={handleChange}
                />
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label htmlFor="submissionDeadline">Submission deadline (date & time, incl. time zone) (if applicable)</label>
                <input
                  type="text"
                  id="submissionDeadline"
                  name="submissionDeadline"
                  value={formData.submissionDeadline}
                  onChange={handleChange}
                  placeholder="e.g., March 15, 2025 5:00 PM EST"
                />
              </div>
            </div>
          )}

          {/* PAGE 3: Grant Experience & Capacity */}
          {currentPage === 3 && (
            <div className="form-page">
              <div className="form-group">
                <label>Who will author the application?</label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="applicationAuthor"
                      value="Our team will author; we want templates + light coaching"
                      checked={formData.applicationAuthor === "Our team will author; we want templates + light coaching"}
                      onChange={handleChange}
                    />
                    <span>Our team will author; we want templates + light coaching</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="applicationAuthor"
                      value="Shared authoring with R3 (facilitated)"
                      checked={formData.applicationAuthor === "Shared authoring with R3 (facilitated)"}
                      onChange={handleChange}
                    />
                    <span>Shared authoring with R3 (facilitated)</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="applicationAuthor"
                      value="Prefer full‑service external writer (INCITE)"
                      checked={formData.applicationAuthor === "Prefer full‑service external writer (INCITE)"}
                      onChange={handleChange}
                    />
                    <span>Prefer full‑service external writer (INCITE)</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="applicationAuthor"
                      value="Not sure yet"
                      checked={formData.applicationAuthor === "Not sure yet"}
                      onChange={handleChange}
                    />
                    <span>Not sure yet</span>
                  </label>
                </div>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label>Grant experience (last 24 months)</label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="grantExperience"
                      value="Submitted 6+ grants"
                      checked={formData.grantExperience === "Submitted 6+ grants"}
                      onChange={handleChange}
                    />
                    <span>Submitted 6+ grants</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="grantExperience"
                      value="Submitted 3–5 grants"
                      checked={formData.grantExperience === "Submitted 3–5 grants"}
                      onChange={handleChange}
                    />
                    <span>Submitted 3–5 grants</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="grantExperience"
                      value="Submitted 1–2 grants"
                      checked={formData.grantExperience === "Submitted 1–2 grants"}
                      onChange={handleChange}
                    />
                    <span>Submitted 1–2 grants</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="grantExperience"
                      value="No submissions yet"
                      checked={formData.grantExperience === "No submissions yet"}
                      onChange={handleChange}
                    />
                    <span>No submissions yet</span>
                  </label>
                </div>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label>Largest grant your team has managed (so far)</label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="largestGrant"
                      value="<$50k"
                      checked={formData.largestGrant === "<$50k"}
                      onChange={handleChange}
                    />
                    <span>&lt;$50k</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="largestGrant"
                      value="$50k–$249k"
                      checked={formData.largestGrant === "$50k–$249k"}
                      onChange={handleChange}
                    />
                    <span>$50k–$249k</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="largestGrant"
                      value="$250k–$999k"
                      checked={formData.largestGrant === "$250k–$999k"}
                      onChange={handleChange}
                    />
                    <span>$250k–$999k</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="largestGrant"
                      value="$1M+"
                      checked={formData.largestGrant === "$1M+"}
                      onChange={handleChange}
                    />
                    <span>$1M+</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="largestGrant"
                      value="Not sure"
                      checked={formData.largestGrant === "Not sure"}
                      onChange={handleChange}
                    />
                    <span>Not sure</span>
                  </label>
                </div>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label>Finance can prepare a workable budget within one week</label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="budgetPreparation"
                      value="Yes"
                      checked={formData.budgetPreparation === "Yes"}
                      onChange={handleChange}
                    />
                    <span>Yes</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="budgetPreparation"
                      value="Not sure / No"
                      checked={formData.budgetPreparation === "Not sure / No"}
                      onChange={handleChange}
                    />
                    <span>Not sure / No</span>
                  </label>
                </div>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label>Availability for weekly 30–45 min check‑ins during the writing period</label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="weeklyCheckIns"
                      value="Yes"
                      checked={formData.weeklyCheckIns === "Yes"}
                      onChange={handleChange}
                    />
                    <span>Yes</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="weeklyCheckIns"
                      value="Not sure / No"
                      checked={formData.weeklyCheckIns === "Not sure / No"}
                      onChange={handleChange}
                    />
                    <span>Not sure / No</span>
                  </label>
                </div>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label>Please check the boxes for all the documents you are able to produce for grant-capture:</label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Certificate of Existence from the Secretary of State"
                      checked={formData.documentsAvailable.includes("Certificate of Existence from the Secretary of State")}
                      onChange={(e) => handleCheckboxChange(e, 'documentsAvailable')}
                    />
                    <span>Certificate of Existence from the Secretary of State</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Letter of Good Standing from your state tax division"
                      checked={formData.documentsAvailable.includes("Letter of Good Standing from your state tax division")}
                      onChange={(e) => handleCheckboxChange(e, 'documentsAvailable')}
                    />
                    <span>Letter of Good Standing from your state tax division</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="IRS Determination Letter"
                      checked={formData.documentsAvailable.includes("IRS Determination Letter")}
                      onChange={(e) => handleCheckboxChange(e, 'documentsAvailable')}
                    />
                    <span>IRS Determination Letter</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Most Recent Audited Financials or IRS Form 990"
                      checked={formData.documentsAvailable.includes("Most Recent Audited Financials or IRS Form 990")}
                      onChange={(e) => handleCheckboxChange(e, 'documentsAvailable')}
                    />
                    <span>Most Recent Audited Financials or IRS Form 990</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Letter of Support - Submit at least one letter of support from a local government authority where the project will be located"
                      checked={formData.documentsAvailable.includes("Letter of Support - Submit at least one letter of support from a local government authority where the project will be located")}
                      onChange={(e) => handleCheckboxChange(e, 'documentsAvailable')}
                    />
                    <span>Letter of Support - Submit at least one letter of support from a local government authority where the project will be located</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Project Leadership Resumes"
                      checked={formData.documentsAvailable.includes("Project Leadership Resumes")}
                      onChange={(e) => handleCheckboxChange(e, 'documentsAvailable')}
                    />
                    <span>Project Leadership Resumes</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Organizational Chart"
                      checked={formData.documentsAvailable.includes("Organizational Chart")}
                      onChange={(e) => handleCheckboxChange(e, 'documentsAvailable')}
                    />
                    <span>Organizational Chart</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="checkbox"
                      value="Board of Directors List"
                      checked={formData.documentsAvailable.includes("Board of Directors List")}
                      onChange={(e) => handleCheckboxChange(e, 'documentsAvailable')}
                    />
                    <span>Board of Directors List</span>
                  </label>
                </div>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label htmlFor="fiscalSponsor">Do you use a fiscal sponsor?</label>
                <input
                  type="text"
                  id="fiscalSponsor"
                  name="fiscalSponsor"
                  value={formData.fiscalSponsor}
                  onChange={handleChange}
                />
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label htmlFor="preferredSupportLane">Preferred support lane (initial)</label>
                <select
                  id="preferredSupportLane"
                  name="preferredSupportLane"
                  value={formData.preferredSupportLane}
                  onChange={handleChange}
                >
                  <option value="">Select option</option>
                  <option value='Self‑service toolkit + "language pack" + final QA (partner‑authored)'>Self‑service toolkit + "language pack" + final QA (partner‑authored)</option>
                  <option value="R3‑facilitated shared authoring (we co‑write; you lead content)">R3‑facilitated shared authoring (we co‑write; you lead content)</option>
                  <option value="Full‑service writing via INCITE (we want a heavier lift)">Full‑service writing via INCITE (we want a heavier lift)</option>
                  <option value="Not sure; please advise">Not sure; please advise</option>
                </select>
              </div>
              <hr className="form-divider" />

              <div className="form-group">
                <label htmlFor="additionalInfo">Anything else we should know?</label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows="4"
                />
              </div>
            </div>
          )}

          {/* PAGE 4: Final Consent */}
          {currentPage === 4 && (
            <div className="form-page">
              <div className="form-group">
                <label>
                  May we share your intake with our partner, INCITE Consulting Solutions, only for the purpose of assessing grant support? <span className="required">*</span>
                </label>
                <div className="checkbox-group">
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="shareWithPartner"
                      value="Yes, you may share with INCITE"
                      checked={formData.shareWithPartner === "Yes, you may share with INCITE"}
                      onChange={handleChange}
                    />
                    <span>Yes, you may share with INCITE</span>
                  </label>
                  <label className="checkbox-option">
                    <input
                      type="radio"
                      name="shareWithPartner"
                      value="No, please keep within R3"
                      checked={formData.shareWithPartner === "No, please keep within R3"}
                      onChange={handleChange}
                    />
                    <span>No, please keep within R3</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          <div className="form-progress">
            <p className="progress-text">Page {currentPage} of 4</p>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${(currentPage / 4) * 100}%` }}></div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="form-navigation">
            {currentPage > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="nav-button prev-button"
              >
                ← Previous
              </button>
            )}
            
            {currentPage < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="nav-button next-button"
              >
                Next →
              </button>
            ) : (
              <button
                type="submit"
                className="nav-button submit-button"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartnerFeedback;
